/* eslint-disable import/no-anonymous-default-export */
import React, { useState, useEffect, useMemo } from 'react';
import { Tooltip, Typography, Menu, MenuItem, ListItemIcon, ListItemText, Icon } from '@mui/material';
import { TaskBannerContainer, InnerContainer, BannerScroll, AddButton, NO_SELECT_CLASS, DISABLED_CLASS, COMPACT_CLASS } from './TaskBanner.styles';
import { get, size, uniqBy, last } from 'global/lib/lodash';
import { getTaskTypeIcon, groupTaskTypesByMode } from '../../task/task-type-utils';
import ReassignModal from '../../task/TaskNotify/ReassignModal';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { userHasPermissions } from 'redux/actions/userLogin';
import { Loader } from 'global/styles';
import { useUser } from 'global/hooks/useUser';
import { Row } from 'global/components/row';
import ShareModal from '../../task/TaskNotify/ShareModal';
import sendData from 'redux/actions/sendData';
import { stringObj, cleanCopy } from '../../../global/utils';
import { Button } from 'global/components/Button';
import clsx from 'clsx';
import { Checkbox } from '../../../global/components/checkbox';
import SnackAlerts from 'global/components/snack-alerts';
import { useTaskTypes } from '../../task/task-type-utils/useTaskTypes';
import { getAvailableTaskTypes, getAvailableTaskTypesForTier, getTaskTypeIdFromTask } from '../../task/task-type-utils';
import { findTaskForItem, findTasksForItem, getInstanceSubScores, getScoreLeaves } from './utils';
import { useDispatch } from 'react-redux';
import { addNewTask } from 'redux/actions/view';
import { CreateModal } from '../../task/CreateModal';

const SELECTALL = 'SELECT_ALL';

export default (props) => {
  const {
    assessment,
    realm_id,
    selectedItems,
    setSelectedItems,
    visible,
    disabled,
    allItems,
    itemPasses,
    showSelectBoxes,
    setShowSelectBoxes,
    assessmentTasks,
    updateAssessmentTasks,
    tasksEnabled = true,
  } = props;
  const [amt, setAmt] = useState({ checkpoints: 0, total: 0, instances: 0, bareCheckpoints: 0 });
  const [showModal, setShowModal] = useState({ open: false });
  const [oboQueue, setOboQueue] = useState([]);
  const [oboIndex, setOboIndex] = useState(0);
  const [oboOpen, setOboOpen] = useState(false);
  const { me } = useUser();
  const reduxDispatch = useDispatch();
  const [userParams] = useState({
    waitFor: props.realm_id,
    build: ['users'],
    params: { filter: { content_realm_id: props.realm_id } },
    useCache: { reload: 1 },
  });
  const [shareLoading, setShareLoading] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false });
  const [typeMenuAnchor, setTypeMenuAnchor] = useState(null);
  const [alerts, setAlerts] = useState();
  const { data: taskTypesData } = useTaskTypes();
  const realmTier = get(assessment, 'realm.tier');
  const availableTypes = getAvailableTaskTypesForTier(getAvailableTaskTypes(taskTypesData), realmTier);
  const singleTypeName = availableTypes.length === 1 ? availableTypes[0].name : null;

  const users = useRetrieveData({ url: '/users' }, userParams);

  const getAmt = () => {
    const checkpoints = Object.keys(selectedItems).filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint');
    const notSelectAll = Object.keys(selectedItems).filter((k) => get(selectedItems, [k, 'id']) !== SELECTALL);
    const checksize = size(checkpoints);
    // A checkpoint with instances is counted as those instances, not as a checkpoint.
    // By Location every selected row is already an instance, so this is the same split.
    let bareCheckpoints = 0;
    let instances = 0;
    checkpoints.forEach((k) => {
      const item = selectedItems[k];
      if (!item) return;
      const units = getScoreLeaves(item);
      if (item.isSubScore || size(getInstanceSubScores(item))) {
        instances += size(units);
      } else {
        bareCheckpoints += 1;
      }
    });

    let totalAmt = 0;

    const checkChildren = (item) => {
      const passed = itemPasses(item);
      if (item.children) {
        item.children.map(checkChildren);
      }
      if (passed) {
        totalAmt += 1;
      }
    };

    allItems.map(checkChildren);

    return {
      checkpoints: checksize,
      bareCheckpoints,
      instances,
      selCheckpoints: checkpoints,
      total: totalAmt,
      totalSelected: size(notSelectAll),
      selectAll: Object.keys(selectedItems).find((k) => get(selectedItems, [k, 'id']) == SELECTALL),
    };
  };

  useEffect(() => {
    setAmt(getAmt());
  }, [selectedItems, allItems]);

  const selectedLeaves = useMemo(() => {
    return Object.keys(selectedItems)
      .filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint')
      .flatMap((k) => getScoreLeaves(selectedItems[k]));
  }, [selectedItems]);

  const { taskedCount, untaskedCount } = useMemo(() => {
    let tasked = 0;
    let untasked = 0;
    selectedLeaves.forEach((leaf) => {
      if (findTaskForItem(leaf, assessmentTasks)) tasked++;
      else untasked++;
    });
    return { taskedCount: tasked, untaskedCount: untasked };
  }, [selectedLeaves, assessmentTasks]);

  // Types that at least one selected score/instance does not already have.
  const createTypes = useMemo(() => {
    if (!size(selectedLeaves)) return availableTypes;
    return availableTypes.filter(
      (tt) =>
        !selectedLeaves.every((leaf) =>
          findTasksForItem(leaf, assessmentTasks).some((t) => String(getTaskTypeIdFromTask(t) || '') === String(tt.id))
        )
    );
  }, [availableTypes, selectedLeaves, assessmentTasks]);

  const createSingleTypeName = createTypes.length === 1 ? createTypes[0].name : null;
  // Parenthetical counts show when create/share applies to fewer units than the
  // selection label (bare checkpoints + instances), not the selected row count.
  const representedCount = amt.bareCheckpoints + amt.instances;
  const countSuffix = (n) => (n !== representedCount ? ` (${n})` : '');

  const shareTasks = () => {
    const checkpoints = Object.keys(selectedItems)
      .filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint')
      .map((k) => selectedItems[k]);
    checkpoints.forEach((cp) => {
      getScoreLeaves(cp).forEach((leaf) => {
        findTasksForItem(leaf, assessmentTasks).forEach((existingTask) => {
          reduxDispatch(addNewTask({ ...existingTask, fromExisting: true }));
        });
      });
    });
    setSelectedItems({});
    setShowSelectBoxes(false);
  };

  const flattenCheckpoints = () => {
    const items = cleanCopy(selectedItems);
    const queue = [];
    Object.keys(items).forEach((k) => {
      const item = get(items, k);
      if (item && item.itemType === 'checkpoint') {
        getScoreLeaves(item).forEach((leaf) => {
          queue.push({ checkpoint: leaf, checklist: item.checklist || leaf.checklist });
        });
      }
    });
    return queue;
  };

  const [oboType, setOboType] = useState(null);

  const startOneByOne = (selectedType) => {
    const queue = flattenCheckpoints().filter(({ checkpoint }) => {
      if (!selectedType) return !findTaskForItem(checkpoint, assessmentTasks);
      return !findTasksForItem(checkpoint, assessmentTasks).some((t) => String(getTaskTypeIdFromTask(t) || '') === String(selectedType.id));
    });
    if (!size(queue)) return;
    setOboType(selectedType || null);
    setOboQueue(queue);
    setOboIndex(0);
    setOboOpen(true);
  };

  const onOboClose = async (created) => {
    if (created && updateAssessmentTasks) {
      await updateAssessmentTasks();
    }
    const nextIndex = oboIndex + 1;
    if (created && nextIndex < oboQueue.length) {
      setOboIndex(nextIndex);
    } else {
      // Done or cancelled - close the sequence
      setOboOpen(false);
      setOboQueue([]);
      setOboIndex(0);
      if (created) {
        setSelectedItems({});
        setShowSelectBoxes(false);
      }
    }
  };

  const addTasks = (selectedType) => {
    const typeToUse = selectedType || (createTypes.length === 1 ? createTypes[0] : availableTypes.length === 1 ? availableTypes[0] : null);
    const isOneByOne = typeToUse && get(typeToUse, 'multi_select_behavior') === 'one_by_one';

    if (isOneByOne) {
      startOneByOne(typeToUse);
      return;
    }

    const assignee = get(props, 'assessment.assignees.0', me);
    const assignees = uniqBy([assignee, ...(users.data || [])], 'id');

    const itemLacksType = (it) => {
      if (!typeToUse) return !findTaskForItem(it, assessmentTasks);
      return !findTasksForItem(it, assessmentTasks).some((t) => String(getTaskTypeIdFromTask(t) || '') === String(typeToUse.id));
    };

    // Filter out checkpoints/instances that already have a task of the selected type
    const filteredItems = {};
    Object.keys(selectedItems).forEach((k) => {
      const item = selectedItems[k];
      if (!item || item.itemType !== 'checkpoint') {
        filteredItems[k] = item;
        return;
      }
      if (item.isSubScore) {
        if (itemLacksType(item)) {
          filteredItems[k] = item;
        }
        return;
      }
      const instancesOnItem = getInstanceSubScores(item);
      if (size(instancesOnItem)) {
        const remaining = instancesOnItem.filter(itemLacksType);
        if (size(remaining)) {
          filteredItems[k] = { ...item, subscores: remaining };
        }
      } else if (itemLacksType(item)) {
        filteredItems[k] = item;
      }
    });

    setShowModal({
      open: true,
      fullScreen: true,
      paperMinWidth: 'md',
      title: typeToUse ? `Create ${typeToUse.name}s` : 'Create Tasks',
      okButton: 'Create',
      tasks: [{ assignees: [assignee] }],
      selectedItems: filteredItems,
      showTrades: true,
      showTaskTypeSelector: false,
      availableTaskTypes: typeToUse ? [typeToUse] : availableTypes,
      onFormSubmit: props.onCreateTasks,
      assignees: assignees,
      onClose: () => {
        setShowModal({ open: false });
        document.getElementsByTagName('body')[0].style = '';
      },
    });
  };

  const printTasks = async () => {
    try {
      if (shareLoading) return;

      setShareLoading(true);

      const checkpoints = Object.keys(selectedItems)
        .filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint')
        .map((k) => selectedItems[k]);
      const scorecard_score_ids = [];
      const sub_score_ids = [];

      checkpoints.forEach((cp) => {
        if (cp.isSubScore) {
          sub_score_ids.push(cp.id);
        } else {
          scorecard_score_ids.push(`${get(cp, 'scorecardScoreId')}`);
        }
      });

      const assessment_id = get(assessment, 'id');
      const assessment_name = get(assessment, 'name');

      const result = await sendData(
        {
          url: `/messages`,
          method: 'post',
          data: {
            data: {
              type: 'messages',
              attributes: stringObj({
                message_type: 'assessment_scores',
                model_type: 'assessment',
                model_id: assessment_id,
                realm_id,
                status: 1,
                body: 'Test',
              }),
              relationships: {
                scorecardScores: {
                  data: scorecard_score_ids.map((id) => ({ type: 'scorecard-scores', id: id.toString() })),
                },
                subScores: size(sub_score_ids)
                  ? { data: sub_score_ids.map((id) => ({ type: 'sub-scores', id: id.toString() })) }
                  : undefined,
              },
            },
          },
        },
        { normalize: { build: ['messages'] }, params: { include: 'media' } }
      );

      const message = last(result);
      const link = get(message, 'media.0.full7DayUrl') || get(message, 'media.0.fullUrl');

      setShareModal({
        open: true,
        link,
        messageId: get(message, 'id'),
        isScorePdf: true,
        title: 'Share Score PDF',
        scoreItems: checkpoints,
        assessmentId: assessment_id,
        assessmentName: assessment_name,
        realmId: realm_id,
        tasks: [],
      });
    } catch (err) {
      console.error(err);
      setAlerts({ message: err?.message || 'Could not generate the PDF for the selected items.', severity: 'error' });
    } finally {
      setShareLoading(false);
      setSelectedItems({});
      setShowSelectBoxes(false);
    }
  };

  const hasPerms = userHasPermissions('createTasks');
  const isZero = amt.checkpoints === 0;
  const indeterminate = !isZero && amt.totalSelected < amt.total;
  const isChecked = amt.totalSelected >= amt.total && amt.totalSelected > 0;

  const onCheckChange = (e) => {
    props.selectItem({
      id: SELECTALL,
      selectAll: indeterminate || isChecked ? false : true,
      checked: e.target.checked,
      value: 100,
      notes: ' ',
    });
  };

  if (!visible) {
    return null;
  }

  const countPhrase = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;
  const selectionLabel = (() => {
    const parts = [];
    if (amt.bareCheckpoints) parts.push(countPhrase(amt.bareCheckpoints, 'checkpoint', 'checkpoints'));
    if (amt.instances) parts.push(countPhrase(amt.instances, 'instance', 'instances'));
    if (!size(parts)) return `${countPhrase(0, 'checkpoint', 'checkpoints')} selected`;
    return `${parts.join(', ')} selected`;
  })();

  const checkbox = (
    <Checkbox
      data-testid="assessment-scores-select-all-checkbox"
      checkProps={{ indeterminate, disabled }}
      checked={isChecked}
      onChange={onCheckChange}
      color="default"
    />
  );

  const actionButtons = showSelectBoxes ? (
    <Row sx={{ ml: 'auto', flexShrink: 0, alignItems: 'center', flexWrap: 'nowrap' }}>
      {tasksEnabled && (
        <>
          {users.loading ? (
            <AddButton>
              <span>
                Loading Users... <Loader size={16} />
              </span>
            </AddButton>
          ) : (
            <>
              {/* Omit the button when the count would be (0) — selected items already have tasks. */}
              {(untaskedCount > 0 || amt.checkpoints === 0) && (size(createTypes) > 0 || taskedCount === 0) && (
                <Tooltip arrow placement="top" title={!hasPerms ? 'You do not have access to create tasks' : createSingleTypeName ? `Create ${createSingleTypeName}s` : 'Create Tasks'}>
                  <AddButton>
                    <Button
                      disabled={disabled || isZero || !hasPerms}
                      data-testid={"assessment-scores-task-create-button"}
                      color="secondary"
                      variant="contained"
                      onClick={(e) => {
                        if (size(createTypes) > 1) {
                          setTypeMenuAnchor(e.currentTarget);
                        } else {
                          addTasks(createTypes[0]);
                        }
                      }}
                      endIcon={size(createTypes) > 1 ? <Icon>arrow_drop_down</Icon> : undefined}
                    >
                      {(createSingleTypeName ? `CREATE ${createSingleTypeName.toUpperCase()}S` : 'CREATE TASKS') + countSuffix(untaskedCount)}
                    </Button>
                  </AddButton>
                </Tooltip>
              )}
              {taskedCount > 0 && (
                <Tooltip arrow placement="top" title="Share existing tasks">
                  <AddButton>
                    <Button disabled={disabled || isZero} color="secondary" variant="contained" onClick={shareTasks}>
                      {(singleTypeName ? `SHARE ${singleTypeName.toUpperCase()}S` : 'SHARE TASKS') + countSuffix(taskedCount)}
                    </Button>
                  </AddButton>
                </Tooltip>
              )}
            </>
          )}
        </>
      )}
      <Tooltip arrow placement="top" title={'Print Items'}>
        <AddButton>
          {users.loading ? (
            <span>
              Loading Users... <Loader size={16} />
            </span>
          ) : (
            <Button disabled={isZero} loading={shareLoading} color="secondary" variant="contained" onClick={printTasks}>
              {'PRINT ITEMS'}
            </Button>
          )}
        </AddButton>
      </Tooltip>
    </Row>
  ) : null;

  const overlays = (
    <>
      <Menu anchorEl={typeMenuAnchor} open={Boolean(typeMenuAnchor)} onClose={() => setTypeMenuAnchor(null)}>
        {(() => {
          const typeGroups = groupTaskTypesByMode(createTypes);
          const renderItem = (tt) => (
            <MenuItem
              key={tt.id}
              data-testid={`assessment-scores-task-create-button-menu-${tt.id}`}
              onClick={() => {
                setTypeMenuAnchor(null);
                addTasks(tt);
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Icon fontSize="small">{getTaskTypeIcon(tt)}</Icon>
              </ListItemIcon>
              <ListItemText>{(tt.name || '').toUpperCase()}</ListItemText>
            </MenuItem>
          );
          if (typeGroups.grouped) {
            return typeGroups.groups.flatMap((group) => [
              <MenuItem key={group.label} disabled sx={{ opacity: '0.7 !important', fontSize: 12 }}>
                {group.label}
              </MenuItem>,
              ...group.items.map(renderItem),
            ]);
          }
          return createTypes.map(renderItem);
        })()}
      </Menu>

      <ReassignModal {...showModal} />

      <ShareModal {...shareModal} onClose={() => setShareModal({ open: false })} actions={{}} />

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />

      {oboOpen && oboQueue[oboIndex] && (
        <CreateModal
          key={`obo-${oboIndex}`}
          open={oboOpen}
          title={`Create ${get(oboType, 'name', singleTypeName) || 'Task'} (${oboIndex + 1} of ${oboQueue.length})`}
          checkpoint={oboQueue[oboIndex].checkpoint}
          checklist={oboQueue[oboIndex].checklist}
          realmId={realm_id}
          realmTier={realmTier}
          assessment={assessment}
          assignedTo={get(assessment, 'assignees', [me])}
          taskTypeId={get(oboType, 'id') || (singleTypeName ? get(availableTypes, '0.id') : undefined)}
          onClose={onOboClose}
        />
      )}
    </>
  );

  return (
    <>
      <TaskBannerContainer className={clsx(!showSelectBoxes && NO_SELECT_CLASS, disabled && DISABLED_CLASS)}>
        <InnerContainer className={clsx(!showSelectBoxes && COMPACT_CLASS)}>
          {checkbox}
          <BannerScroll>
            {showSelectBoxes ? (
              <>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{selectionLabel}</Typography>
                {actionButtons}
              </>
            ) : (
              props.children
            )}
          </BannerScroll>
        </InnerContainer>
      </TaskBannerContainer>
      {overlays}
    </>
  );
};
